/**
 * Updates the Sentry feedback form inputs with user information
 * @param email User's email
 * @param firstName User's first name (optional)
 * @param lastName User's last name (optional)
 */
export const updateSentryFeedbackFormInputs = (email: string, firstName?: string, lastName?: string): void => {
  try {
    // Get the shadow host element
    const feedbackHost = document.getElementById('sentry-feedback');
    if (!feedbackHost || !feedbackHost.shadowRoot) return;

    // Access inputs inside shadow DOM
    const emailInput = feedbackHost.shadowRoot.querySelector('input[name="email"]');
    const nameInput = feedbackHost.shadowRoot.querySelector('input[name="name"]');

    // Update email input if it exists
    if (emailInput) {
      (emailInput as HTMLInputElement).value = email;
    }

    // Update name input if it exists
    if (nameInput) {
      // Format name properly if first_name and last_name are provided
      const userName = firstName || lastName ? `${firstName || ''} ${lastName || ''}`.trim() : email;
      (nameInput as HTMLInputElement).value = userName;
    }
  } catch (error) {
    // Silent error handling to prevent app crashes
    console.error('Failed to update Sentry feedback form inputs:', error);
  }
};

export const updateFormStyling = () => {
  try {
    // Get the shadow host element
    const feedbackHost = document.getElementById('sentry-feedback');
    if (!feedbackHost || !feedbackHost.shadowRoot) return;

    // Create the scrollbar style element right away
    const styleElement = document.createElement('style');
    const uniqueClass = `textarea-custom-${Date.now()}`;

    // Define the scrollbar styling with completely transparent track
    styleElement.textContent = `
      .${uniqueClass} {
        height: 138px;
        width: 272px;
        resize: none;
        overflow-y: scroll;
        scrollbar-color: #888 transparent;
      }
      
      .${uniqueClass}::-webkit-scrollbar-track {
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      
      .${uniqueClass}::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
        min-height: 30px;
      }
      
      .${uniqueClass}::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
      
      /* Safari-specific styling */
      @supports (-webkit-hyphens:none) {
        .${uniqueClass} {
          padding-right: 0px;
        }      
        .${uniqueClass}::-webkit-scrollbar {
          background-color: transparent !important;
        }
        .${uniqueClass}::-webkit-scrollbar-track,
        .${uniqueClass}::-webkit-scrollbar-track-piece {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
        }
        .${uniqueClass}::-webkit-scrollbar-thumb {
          background-color: #888;
          border: 2px solid transparent;
          border-radius: 10px;
          background-clip: content-box;
        }
      }
    `;

    // Add the style element to the shadow DOM immediately
    feedbackHost.shadowRoot.appendChild(styleElement);

    // Access inputs inside shadow DOM
    const emailInput = feedbackHost.shadowRoot.querySelector('input[name="email"]');
    const nameInput = feedbackHost.shadowRoot.querySelector('input[name="name"]');
    const messageTextare = feedbackHost.shadowRoot.querySelector('textarea[name="message"]');

    // Update email input if it exists
    if (emailInput) {
      (emailInput as HTMLInputElement).setAttribute('disabled', 'true');
    }

    // Update name input if it exists
    if (nameInput) {
      (nameInput as HTMLInputElement).setAttribute('disabled', 'true');
    }

    // Update textarea if it exists
    if (messageTextare) {
      // Add the class to the textarea so it picks up the styles
      messageTextare.classList.add(uniqueClass);

      // Force a refresh of the styles
      setTimeout(() => {
        (messageTextare as HTMLTextAreaElement).style.overflowY = 'hidden';
        setTimeout(() => {
          (messageTextare as HTMLTextAreaElement).style.overflowY = 'scroll';
        }, 10);
      }, 0);
    }
  } catch (error) {
    // Silent error handling to prevent app crashes
    console.error('Failed to update Sentry feedback form inputs:', error);
  }
};
